import {Alert, Anchor, Breadcrumbs, Select, Stack} from "@mantine/core";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link, useParams} from "react-router";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import Page from "../../components/Page/Page.tsx";
import api from "../../utils/api";
import RouteDetails from "./RouteDetails.tsx";
import {RouteGroup} from "./types.ts";
import usePresets from "./usePresets.ts";

function RouteDetailsPage() {
  const {id} = useParams<{id: string}>();
  const [route, setRoute] = useState<RouteGroup | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);

  const {presets, defaultPresetId, isSuccess: presetsLoaded} = usePresets();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [skill, setSkill] = useState<number | undefined>(0);
  const [hyper, setHyper] = useState<number | undefined>(1);
  const [sublight, setSublight] = useState<number | undefined>(10);
  const presetDefaultsApplied = useRef<boolean>(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    setLoading(true);
    setNotFound(false);
    api.get('/hyper/v2/routes').then(res => {
      const found = (res.data?.routes ?? []).find((r: RouteGroup) => r.id === id) ?? null;
      if (!found) {
        setNotFound(true);
      }
      setRoute(found);
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (presetDefaultsApplied.current || !presetsLoaded) {
      return;
    }
    presetDefaultsApplied.current = true;

    if (!defaultPresetId) {
      return;
    }
    const preset = presets.find(p => p.id === defaultPresetId);
    if (preset) {
      setSelectedPresetId(preset.id);
      setSkill(preset.skill);
      setHyper(preset.hyper);
      setSublight(preset.sublight);
    }
  }, [presetsLoaded, defaultPresetId, presets]);

  const presetOptions = useMemo(
    () => presets.map(p => ({value: p.id, label: p.name})),
    [presets]
  );

  const onSelectPreset = useCallback((presetId: string | null) => {
    if (!presetId) {
      setSelectedPresetId(null);
      return;
    }
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setSelectedPresetId(preset.id);
      setSkill(preset.skill);
      setHyper(preset.hyper);
      setSublight(preset.sublight);
    }
  }, [presets]);

  const onRouteUpdate = useCallback((updated: RouteGroup) => {
    setRoute(prev => prev ? {...prev, ...updated} : updated);
  }, []);

  if (loading) {
    return <LoaderScreen message="Loading hyper route" />;
  }

  const presetControl = (
    <Select
      label="Preset"
      value={selectedPresetId}
      data={presetOptions}
      onChange={onSelectPreset}
      placeholder="Select preset"
      clearable
    />
  );

  return (
    <Page>
      <title>{route ? `${route.name} – Hyper Route` : 'Hyper Route'}</title>

      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to="/hl/routes">Hyper Routes</Anchor>
          <Anchor component={Link} to={`/hl/routes/${id}`}>{route?.name ?? 'Route not found'}</Anchor>
        </Breadcrumbs>

        {notFound ? (
          <Alert color="yellow" title="Route not found">
            No hyper route matched the selected id.
          </Alert>
        ) : route ? (
          <RouteDetails
            route={route}
            skill={skill}
            hyper={hyper}
            sublight={sublight}
            onSkillChange={setSkill}
            onHyperChange={setHyper}
            onSublightChange={setSublight}
            onRouteUpdate={onRouteUpdate}
            presetControl={presetControl}
          />
        ) : null}
      </Stack>
    </Page>
  );
}

export default withRouteErrorBoundary(RouteDetailsPage);
